import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { useAuth } from "@/hooks/auth";
import { useProfileCompletion } from "@/hooks/useProfileCompletion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { GenericPageHero } from "@/components/shared/GenericPageHero";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ExperienceSection } from "@/components/profile/ExperienceSection";
import { EducationSection } from "@/components/profile/EducationSection";
import { ProjectsSection } from "@/components/profile/ProjectsSection";
import { CertificationsSection } from "@/components/profile/CertificationsSection";
import { AwardsSection } from "@/components/profile/AwardsSection";
import { ImportResumeDialog } from "@/components/profile/ImportResumeDialog";
import { 
  Edit, Upload, FileText, Briefcase, GraduationCap, FolderOpen, 
  Award, BadgeCheck, User, AlertCircle
} from "lucide-react";
import { PageShell } from "@/components/ui/progressive-loading";
import { useTranslation } from "@/hooks/useTranslation";

const SimpleProfile = () => {
  const { user, isReady } = useAuth();
  const toast = useTranslatedToast();
  const { completionPercentage, isComplete, loading: completionLoading } = useProfileCompletion();
  const { t } = useTranslation();
  const [profile, setProfile] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch user profile data
  useEffect(() => {
    if (!user || !isReady) return;

    const fetchProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching profile:', error);
          return;
        }

        setProfile(data || { email: user.email });
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchProfile();
  }, [user, isReady]);

  const handleProfileUpdate = async () => {
    // Refetch profile data without page reload
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error);
        return;
      }

      setProfile(data || { email: user.email });
      
      // Force re-render of all profile sections by triggering a window event
      // This will cause the CertificationsSection and AwardsSection components to refresh their data
      window.dispatchEvent(new CustomEvent('profileDataUpdated'));
      
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  return (
    <PageShell>
      <GenericPageHero
        icon={User}
        title={t('pages:profile.title')}
        description={t('pages:profile.description')}
        stats={[
          { 
            value: completionLoading ? "..." : `${completionPercentage}%`, 
            label: t('pages:profile.completion')
          }
        ]}
        heroPrefix="profile"
      />

      {/* Profile Header */}
      <ProfileHeader
        profile={profile}
        user={user}
        isEditing={isEditing}
        onEditToggle={() => setIsEditing(!isEditing)}
        onProfileUpdate={handleProfileUpdate}
      />

      {/* Profile Sections */}
      <div className="grid gap-4">
        {/* Experience Section */}
        <Card className="hover:shadow-sm transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Briefcase className="w-4 h-4 text-muted-foreground" />
              {t('pages:profile.work_experience')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ExperienceSection userId={user?.id} isEditing={isEditing} />
          </CardContent>
        </Card>

        {/* Education Section */}
        <Card className="hover:shadow-sm transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <GraduationCap className="w-4 h-4 text-muted-foreground" />
              {t('pages:profile.education')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <EducationSection userId={user?.id} isEditing={isEditing} />
          </CardContent>
        </Card>

        {/* Projects Section */}
        <Card className="hover:shadow-sm transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <FolderOpen className="w-4 h-4 text-muted-foreground" />
              {t('pages:profile.projects')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ProjectsSection userId={user?.id} isEditing={isEditing} />
          </CardContent>
        </Card>

        {/* Certifications Section */}
        <Card className="hover:shadow-sm transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <BadgeCheck className="w-4 h-4 text-muted-foreground" />
              {t('pages:profile.certifications')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CertificationsSection userId={user?.id} isEditing={isEditing} />
          </CardContent>
        </Card>

        {/* Awards Section */}
        <Card className="hover:shadow-sm transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Award className="w-4 h-4 text-muted-foreground" />
              {t('pages:profile.awards')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <AwardsSection userId={user?.id} isEditing={isEditing} />
          </CardContent>
        </Card>
      </div>
    </PageShell>
  );
};

export default SimpleProfile;